import { useMemo } from 'react'
import { Typography } from '../../../core'
import { styled } from '../../../styles'
import { BaseThemeType, useMediaQuery } from '../../../theme'
import { MarketplaceQuotationTableBreakdownItem } from '../types'
import { formatCurrencyWithSymbol } from '../utils'

type PropTypes = {
  tableRow: MarketplaceQuotationTableBreakdownItem
  currencySymbol: string
}

const Row = styled('div')<{ highlight: boolean }>(
  ({ theme, highlight }: { theme: BaseThemeType; highlight: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    color: theme.palette.grey[800],
    fontWeight: highlight ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
    marginBottom: '4px',
  })
)

const Left = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  flex: 1,
})

const Right = styled('div')<{ highlight: boolean }>(
  ({ theme, highlight }: { theme: BaseThemeType; highlight: boolean }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flex: 1,
    color: theme.palette.grey[800],
    fontWeight: highlight ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  })
)

const MainDisplay = styled(Typography)<{ highlight: boolean }>(
  ({ theme, highlight }: { theme: BaseThemeType; highlight: boolean }) => ({
    color: theme.palette.grey[700],
    fontWeight: highlight ? theme.typography.fontWeightBold : theme.typography.fontWeightRegular,
  })
)

const ExtraDisplay = styled(Typography)(({ theme }: { theme: BaseThemeType }) => ({
  color: theme.palette.grey[700],
  fontWeight: theme.typography.fontWeightRegular,
}))

const FinanceMarketplaceBreakdownTableRow = (props: PropTypes) => {
  const { tableRow, currencySymbol } = props
  const isMobile = useMediaQuery((theme: BaseThemeType) => theme.breakpoints.down('sm'))

  const formattedValue = useMemo(() => {
    if (tableRow?.fixed_value) return tableRow.fixed_value
    else if (tableRow?.value_format === 'currency') {
      return formatCurrencyWithSymbol(tableRow.value, currencySymbol, 'en-US', tableRow.significant_digits || 0)
    }
    if (tableRow?.value_format === 'number') {
      // If value is a number, round to significant_digits, else return as is
      if (typeof tableRow.value === 'number' && typeof tableRow.significant_digits === 'number') {
        return Number(tableRow.value).toFixed(tableRow.significant_digits)
      }
      return tableRow.value
    } else if (tableRow?.value_format === 'string') {
      return tableRow.value
    }
    return tableRow.value
  }, [tableRow])
  return (
    <Row highlight={Boolean(tableRow.highlight_value)}>
      <Left>
        <MainDisplay highlight={Boolean(tableRow.highlight_label)} textVariant={isMobile ? 'body1' : 'body2'}>
          {tableRow.label}
        </MainDisplay>
        {tableRow.label_extra && <ExtraDisplay textVariant="caption1">{tableRow.label_extra}</ExtraDisplay>}
      </Left>
      <Right highlight={Boolean(tableRow.highlight_value)}>
        <MainDisplay highlight={Boolean(tableRow.highlight_value)} textVariant={isMobile ? 'body1' : 'body2'}>
          {formattedValue}
        </MainDisplay>
        {tableRow.value_extra && <ExtraDisplay textVariant="caption1">{tableRow.value_extra}</ExtraDisplay>}
      </Right>
    </Row>
  )
}

export default FinanceMarketplaceBreakdownTableRow
